// Shared brand-kit heuristics used by both the Dashboard Brand Kit builder (ExtractBrandTool)
// and the Codeo website-brand analyzer. Both hit the same extract-brand-kit worker, so the
// logic for ranking logo candidates, converting them to LLM-native images, and cleaning the
// color palette lives here once.

export interface LogoLike {
    src: string;
    source: string;
    alt?: string | null;
    width?: number | null;
    height?: number | null;
}

// The extract-brand-kit worker tags each logo with where it found it. Best-to-worst: the
// site's OWN mark — inside the brand/home link or a CSS-background on a logo element — beats a
// logo-named <img> (which can be a customer/partner logo elsewhere on the page), which beats a
// header image (often a hero/stock photo). Favicons and og-image rank low-but-known so they
// still beat chrome glyphs; svg-icon (menu/arrows/chevrons) is last. Mirrors the worker's order.
export const LOGO_SOURCE_PRIORITY = [
    'home-link-img',
    'home-link-svg',
    'logo-bg',
    'logo-attr',
    'header-img',
    'svg-logo',
    'json-ld',
    'json-ld-image',
    'apple-touch-icon',
    'icon',
    'manifest',
    'og-image',
    'svg-icon',
];

const SUPPORTED_IMAGE_EXTENSIONS = /\.(png|jpe?g|gif|webp|ico|svg)(\?.*)?$/i;
const LLM_NATIVE_IMAGE_EXTENSIONS = /\.(png|jpe?g|gif|webp)(\?.*)?$/i;
const LLM_SUPPORTED_MIME_TYPES = /^data:image\/(png|jpeg|gif|webp)[;,]/;

// A logo we can actually render into an <img> (and therefore into an LLM image block):
// a data:image/ URL, or an http(s) URL ending in a known image extension.
export function isInjectableLogo(src: string): boolean {
    if (src.startsWith('data:image/')) {
        return true;
    }
    if (!src.startsWith('http://') && !src.startsWith('https://')) {
        return false;
    }
    return SUPPORTED_IMAGE_EXTENSIONS.test(new URL(src).pathname);
}

function logoSourceRank(source: string): number {
    const s = source.toLowerCase();
    // Favicons arrive as dynamic strings: "shortcut icon", "apple-touch-icon 180x180".
    if (s.includes('apple-touch-icon')) return LOGO_SOURCE_PRIORITY.indexOf('apple-touch-icon');
    if (s.includes('shortcut icon')) return LOGO_SOURCE_PRIORITY.indexOf('icon');
    const i = LOGO_SOURCE_PRIORITY.indexOf(source);
    return i === -1 ? LOGO_SOURCE_PRIORITY.length : i;
}

// Injectable logos, ranked by source priority, truncated to `max`. Original array order is
// preserved within a rank (explicit index tie-break), so callers can recover each logo's
// index via indexOf.
export function getTopLogoCandidates<T extends LogoLike>(logos: T[], max: number): T[] {
    return logos
        .filter((l) => isInjectableLogo(l.src))
        .map((logo, index) => ({ logo, index, rank: logoSourceRank(logo.source) }))
        .sort((a, b) => a.rank - b.rank || a.index - b.index)
        .map((d) => d.logo)
        .slice(0, max);
}

// LLMs accept png/jpeg/gif/webp. Anything else (svg, ico, exotic data URLs) must be
// rasterized first.
export function needsConversion(src: string): boolean {
    if (src.startsWith('data:')) {
        return !LLM_SUPPORTED_MIME_TYPES.test(src);
    }
    try {
        return !LLM_NATIVE_IMAGE_EXTENSIONS.test(new URL(src).pathname);
    } catch {
        return false;
    }
}

// Browser-only (canvas). Rasterize an svg/ico/remote image to a downscaled PNG data URL the
// LLM can read. Resolves null if the image can't be loaded.
export function convertImageToDataUrl(src: string, maxWidth = 256): Promise<string | null> {
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            const scale = Math.min(1, maxWidth / img.width);
            const canvas = document.createElement('canvas');
            canvas.width = Math.round(img.width * scale);
            canvas.height = Math.round(img.height * scale);
            const ctx = canvas.getContext('2d')!;
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = () => resolve(null);
        img.src = src;
    });
}

// Browser-only (canvas). Shrink a data URL to `maxWidth` and re-encode as JPEG to cut payload
// size before sending to the LLM. Resolves the original on failure.
export function downscaleDataUrl(dataUrl: string, maxWidth: number): Promise<string> {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const scale = Math.min(1, maxWidth / img.width);
            const canvas = document.createElement('canvas');
            canvas.width = Math.round(img.width * scale);
            canvas.height = Math.round(img.height * scale);
            const ctx = canvas.getContext('2d')!;
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL('image/jpeg', 0.7));
        };
        img.onerror = () => resolve(dataUrl);
        img.src = dataUrl;
    });
}

export function hexToHsl(hex: string): [number, number, number] {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;

    if (max === min) return [0, 0, l * 100];

    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h = 0;
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;

    return [h * 360, s * 100, l * 100];
}

// Drop near-black/near-white and near-duplicate colors, preserving input order so a caller's
// priority ordering survives. Non-7-char hex values are skipped.
export function deduplicateColors(hexColors: string[]): string[] {
    const kept: { hex: string; hsl: [number, number, number] }[] = [];

    for (const hex of hexColors) {
        if (hex.length !== 7) continue;
        const hsl = hexToHsl(hex);

        // Skip near-black and near-white
        if (hsl[2] < 8 || hsl[2] > 95) continue;

        // Skip if too close to an already-kept color
        const tooClose = kept.some((existing) => {
            const hueDiff = Math.abs(existing.hsl[0] - hsl[0]);
            const hueDistance = Math.min(hueDiff, 360 - hueDiff);
            const lightDiff = Math.abs(existing.hsl[1] - hsl[1]);
            // Low-saturation colors: compare by lightness only
            if (hsl[1] < 10 && existing.hsl[1] < 10) {
                return Math.abs(existing.hsl[2] - hsl[2]) < 20;
            }
            return hueDistance < 20 && lightDiff < 20;
        });

        if (!tooClose) {
            kept.push({ hex, hsl });
        }
    }

    return kept.map((c) => c.hex);
}
